import {type SxProps, TooltipProps, Typography} from "@mui/material"
import {useEffect, useRef, useState, type FC} from "react"
import {truncateText} from "../../../helpers"
import {Tooltip} from "../../atoms"

type CharacterCountProps = {
  text: string
  truncationType?: "characterCount"
  limit: number
  tooltipStyle?: SxProps
  textStyle?: SxProps
  tooltipProps?: Omit<TooltipProps, "title" | "children">
  includeEllipsis?: boolean
}

type WidthProps = {
  text: string
  truncationType: "width"
  limit?: undefined
  tooltipStyle?: SxProps
  textStyle?: SxProps
  tooltipProps?: Omit<TooltipProps, "title" | "children">
  includeEllipsis?: boolean
}

type Props = CharacterCountProps | WidthProps

const TextWithTooltip: FC<Props> = (props) => {
  const {
    text,
    truncationType = "characterCount",
    limit,
    tooltipStyle,
    textStyle,
    tooltipProps,
    includeEllipsis = true,
  } = props

  const textRef = useRef<HTMLDivElement | null>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth)
    }
  }, [text])

  if (truncationType === "characterCount") {
    if (limit && text.length > limit) {
      return (
        <Tooltip title={text} sx={tooltipStyle} {...tooltipProps}>
          <Typography sx={textStyle}>
            {truncateText(text, limit, "...", includeEllipsis)}
          </Typography>
        </Tooltip>
      )
    }
  } else if (truncationType === "width") {
    return (
      <Tooltip
        title={isTruncated ? text : ""}
        sx={tooltipStyle}
        {...tooltipProps}
      >
        <Typography
          ref={textRef}
          fontSize="inherit"
          color="inherit"
          component="span"
          sx={{
            fontFamily: ["Open Sans", "Arial", "sans-serif"].join(","),
            display: "inline-block",
            maxWidth: "100%",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            ...textStyle,
          }}
        >
          {text}
        </Typography>
      </Tooltip>
    )
  }

  return (
    <Typography sx={textStyle} component="span">
      {text}
    </Typography>
  )
}

export default TextWithTooltip
